/**
 * resolveRefMap — render-time `[Ref N]` → source resolution.
 *
 * THE BUG (Holly Ridge QA 2026-06-10): every report shipped 49-61 orphaned
 * inline `[Ref N]` citations. Two render-time couplings produced them:
 *
 *   1. `computeAppendixVisibleRefIndices` (used to decide whether an in-text
 *      `[Ref N]` resolves) drops every row flagged `citedInThisReport === false`.
 *      But the APPENDIX renderer (`ReferencesSection`) has a POPULATE FALLBACK:
 *      when the cited-only filter would yield ZERO rows it surfaces ALL
 *      non-sentinel refs instead (the gen-time `markCitations` pass routinely
 *      mis-flags every row uncited — see `renumberReferences.ts`). So the
 *      appendix renders a `#ref-N` anchor while the in-text resolver, applying
 *      the stricter predicate, marks the SAME token out-of-bounds and renders
 *      `[Ref ?]`. The marker desyncs from what actually renders below it.
 *
 *   2. An in-bounds `[Ref N]` whose row exists in `report.references` (and gets
 *      a real `#ref-N` appendix anchor) must resolve — orphaning it is wrong.
 *
 * This helper computes the SAME effective-visible index set the appendix
 * actually renders (mirroring the POPULATE fallback), and exposes a
 * number → source map so callers can resolve `[Ref N]` against the row that
 * truly ships. It is pure and side-effect free; it does NOT fabricate sources
 * (a genuinely hallucinated `N > references.length` still has no row and stays
 * degraded).
 *
 * Anchors are 1-based on the ORIGINAL `report.references` position
 * (`#ref-{origIdx+1}`), matching `ReferencesSection` + `RefLink`.
 */
import type { DataSource, SiteRagReport } from '../../types/siteRag';
import {
  isSentinelReference,
  type AppendixVisibilityOptions,
} from '../appendixCitationIndex';
import { isSentimentInfluencingReference } from '../filterSentimentReferences';

/**
 * The set of 1-based `report.references` indices that the appendix ACTUALLY
 * renders (so the in-text `[Ref N]` marker agrees with the rendered rows).
 *
 * Mirrors `ReferencesSection`'s non-editing render predicate EXACTLY, including
 * its POPULATE fallback:
 *   - always drop sentinel rows (source = "unavailable"/"failed"/"default
 *     estimate"),
 *   - for Public-Sentiment reports drop non-influencing rows,
 *   - then drop `citedInThisReport === false` rows — UNLESS that would empty the
 *     list, in which case surface ALL surviving (non-sentinel/non-PSR-dropped)
 *     rows (the appendix's `citedOnly.length > 0 ? citedOnly : refsWithIdx`).
 *
 * Returns `null` when there are no references — callers treat null as "no
 * appendix constraint" and fall back to a plain length-bounds check, matching
 * the prior `computeAppendixVisibleRefIndices` contract.
 */
export function computeEffectiveAppendixVisibleRefIndices(
  references: readonly DataSource[] | undefined | null,
  opts: AppendixVisibilityOptions = {},
): Set<number> | null {
  if (!references || references.length === 0) return null;
  const isPsr = opts.reportType === 'public-sentiment';

  // Stage 1 — the always-applied appendix filters (sentinel + PSR-influence).
  // These are the rows that get a `#ref-N` anchor at all.
  const anchored: number[] = [];
  references.forEach((ref, idx) => {
    if (!ref) return;
    if (isSentinelReference(ref)) return;
    if (isPsr && !isSentimentInfluencingReference(ref)) return;
    anchored.push(idx + 1); // 1-based
  });
  if (anchored.length === 0) return new Set();

  // Stage 2 — the cited-only sub-filter, with the POPULATE fallback: if it
  // would empty the rendered list, keep every anchored row (the appendix does
  // exactly this so it never hides a citation target the body points at).
  const citedOnly = anchored.filter((n) => references[n - 1]?.citedInThisReport !== false);
  return new Set(citedOnly.length > 0 ? citedOnly : anchored);
}

/**
 * Build a `[Ref N]` → source map (1-based) for the rows that actually render in
 * the appendix. The map only contains resolvable numbers, so a caller can both
 * (a) resolve a token to its source and (b) detect an orphan (`!map.has(n)`).
 */
export function buildRefNumberSourceMap(
  report: Pick<SiteRagReport, 'references' | 'reportType'>,
): Map<number, DataSource> {
  const refs = report.references ?? [];
  const visible = computeEffectiveAppendixVisibleRefIndices(refs, {
    reportType: report.reportType,
  });
  const map = new Map<number, DataSource>();
  if (refs.length === 0) return map;
  // null ⇒ no appendix constraint (handled above by the empty-refs guard, but
  // defensive): fall back to every in-bounds row.
  const allow = visible ?? new Set(refs.map((_, i) => i + 1));
  for (const n of allow) {
    const ref = refs[n - 1];
    if (ref) map.set(n, ref);
  }
  return map;
}
